/**
 * TEMP (CDMX-19259): traces every mutation of the auth `channel` key in
 * sessionStorage, with a stack, so a missing channel can be attributed to the
 * code that removed it rather than guessed at.
 *
 * Deliberately imports nothing and uses `console.warn` directly. This installs
 * from the eager entry chunk, and pulling in the `@codametrix/ui-common` barrel
 * for `loggerFactory` here would evaluate the auth channel module earlier than
 * it otherwise would — changing the very boot ordering under investigation.
 * `console` also survives an elevated UI_LOG_LEVEL, which filters logger output.
 */

const PREFIX = '[SessionStorageObserver]';

/** Keys noisy enough to matter are skipped; only channel identity is traced. */
const WATCHED_KEYS = new Set(['channel']);

declare global {
  interface Window {
    __CMX_SESSION_STORAGE_OBSERVED__?: boolean;
  }
}

const trace = (event: string, ...args: unknown[]) => {
  console.warn(`${PREFIX} ${event}`, ...args, new Error().stack);
};

const installSessionStorageObserver = () => {
  if (
    typeof window === 'undefined' ||
    window.__CMX_SESSION_STORAGE_OBSERVED__ === true
  ) {
    return;
  }

  try {
    const storage = window.sessionStorage;
    const setItem = storage.setItem.bind(storage);
    const removeItem = storage.removeItem.bind(storage);
    const clear = storage.clear.bind(storage);

    storage.setItem = (key: string, value: string) => {
      if (WATCHED_KEYS.has(key)) {
        trace(`setItem('${key}')`, 'from=', storage.getItem(key), 'to=', value);
      }
      setItem(key, value);
    };

    storage.removeItem = (key: string) => {
      if (WATCHED_KEYS.has(key)) {
        trace(`removeItem('${key}')`, 'was=', storage.getItem(key));
      }
      removeItem(key);
    };

    // Unconditional: clear() takes the watched keys with it whatever the caller
    // was actually trying to reset.
    storage.clear = () => {
      const watched = Array.from(WATCHED_KEYS)
        .map(key => `${key}=${storage.getItem(key)}`)
        .join(' ');
      trace('clear()', watched);
      clear();
    };

    window.__CMX_SESSION_STORAGE_OBSERVED__ = true;
  } catch (err) {
    console.warn(`${PREFIX} failed to install`, err);
  }
};

installSessionStorageObserver();
